/**
 * §P.9 · Asistencia por grupo y franja.
 *
 * ## Es el único informe que ve el entrenador
 *
 * Y es coherente con todo §P: el entrenador no ve el dinero de nadie, pero sí quién
 * viene a su clase. `GET /reports/attendance` declara `coa`; los otros ocho, no.
 *
 * ## Una franja, una fila
 *
 * La franja sale de la hora a la que **se dio** la clase y no de la que estaba en el
 * horario: un grupo que se mueve de las 17:00 a las 18:00 en enero son dos franjas, y
 * juntarlas esconde el motivo del cambio.
 *
 * Y el informe llegó a devolver una fila por clase dada en lugar de una por franja —un
 * `GROUP BY` que resolvía al nombre de la columna en vez de al alias—. Se vio aquí, al
 * pintar ocho filas idénticas, y se arregló en el repositorio. Lo cuento porque explica
 * por qué esta pantalla enseña `sessions_held`: una franja con ocho clases y una con una
 * no se leen igual, y verlo en pantalla es lo que destapó el fallo.
 *
 * ## Sin nadie marcado no hay porcentaje
 *
 * Un 0 % en una franja donde no se pasó lista acusa al grupo de no venir, cuando el que
 * no vino fue el dato. La pantalla dice «sin pasar lista», que es otra cosa.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, opcionesDeSesion } from '@/shared/api/client';
import { descargarFichero } from '@/shared/api/descargas';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { DIAS } from '@/club/grupos/tipos';
import {
  porcentajeDeAsistencia,
  type FilaDeAsistencia,
  type Informe,
} from '@/club/informes/tipos';

/** El color según cuánto se viene. Tres tramos, no un degradado: es para decidir. */
const claseDeTasa = (pct: number): string =>
  pct >= 80 ? 'pastilla-bien' : pct >= 60 ? 'pastilla-paused' : 'pastilla-mal';

export function Asistencia({ club }: { club: string }) {
  const [problema, setProblema] = useState<Problema | null>(null);
  const [descargando, setDescargando] = useState(false);

  const informe = useQuery({
    queryKey: ['informe-asistencia', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .reports.attendance.get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Informe<FilaDeAsistencia>;
    },
  });

  const descargar = async () => {
    setDescargando(true);
    setProblema(null);
    const fallo = await descargarFichero(
      `/v1/clubs/${club}/reports/attendance?format=csv`,
      'informe-de-asistencia.csv',
      opcionesDeSesion
    );
    setDescargando(false);
    if (fallo) setProblema(fallo);
  };

  if (informe.isPending) {
    return <Cargando filas={6} etiqueta="Calculando la asistencia" />;
  }

  if (informe.isError || !informe.data) {
    return (
      <PanelError
        problema={aProblema(informe.error)}
        reintentar={() => void informe.refetch()}
      />
    );
  }

  const filas = informe.data.rows;
  const totales = informe.data.totals;
  const marcados = totales['marked'] ?? 0;
  const presentes = totales['present'] ?? 0;
  const tasaGlobal =
    marcados === 0 ? null : Math.round((presentes / marcados) * 100);

  return (
    <section>
      <header className="titulo-fila">
        <h1>Asistencia</h1>
        <button
          type="button"
          onClick={() => void descargar()}
          disabled={descargando}
        >
          {descargando ? 'Preparando…' : 'Descargar en CSV'}
        </button>
      </header>

      <p className="tenue">
        Por grupo y por franja, de las clases que se dieron. La hora es la que
        realmente tuvo la clase, no la del horario.
      </p>

      {problema && <PanelError problema={problema} />}

      {filas.length === 0 ? (
        <Vacio
          titulo="Todavía no hay clases dadas"
          explicacion="Este informe se llena con las clases que se marcan como dadas y con la lista que se pasa en ellas. Genera el calendario del mes y empieza a pasar lista."
        />
      ) : (
        <>
          <dl className="datos">
            <div>
              <dt>Clases dadas</dt>
              <dd>{totales['sessions_held'] ?? 0}</dd>
            </div>
            <div>
              <dt>Asistencia media</dt>
              {/* `null` y no «0 %»: sin listas pasadas no hay media que dar. */}
              <dd>
                {tasaGlobal === null ? 'Sin pasar lista' : `${tasaGlobal} %`}
              </dd>
            </div>
            <div>
              <dt>Faltas</dt>
              <dd>{totales['absent'] ?? 0}</dd>
            </div>
            <div>
              <dt>Justificadas</dt>
              <dd>{totales['excused'] ?? 0}</dd>
            </div>
          </dl>

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Grupo</th>
                  <th>Franja</th>
                  <th className="num">Clases</th>
                  <th className="num">Presentes</th>
                  <th className="num">Faltas</th>
                  <th className="num">Justificadas</th>
                  <th>Asistencia</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => {
                  const pct = porcentajeDeAsistencia(f);

                  return (
                    <tr key={`${f.group_id}-${f.weekday}-${f.starts_at}`}>
                      <td>{f.group_name}</td>
                      <td>
                        {/* `weekday` es ISO: `DIAS[1]` es lunes. Nunca `getDay()`. */}
                        {DIAS[f.weekday] ?? `Día ${f.weekday}`} · {f.starts_at}
                      </td>
                      <td className="num">{f.sessions_held}</td>
                      <td className="num">{f.present}</td>
                      <td className="num">{f.absent}</td>
                      <td className="num">{f.excused}</td>
                      <td>
                        {pct === null ? (
                          <span className="tenue">Sin pasar lista</span>
                        ) : (
                          <span className={`pastilla ${claseDeTasa(pct)}`}>
                            {pct} %
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
